import {Fragment, useMemo, useState} from "react";
import {PhoneHQLoader, PhoneHQButton, PhoneHQDialog, PhoneHQDialogFooter, PhoneHQInput} from 'ch3-ui-lib';
import {ArrowRight, FileText, KeyRound, Mic, Pencil, Plus, Search, Trash2, Users} from "lucide-react";
import {
    useCreateEnsAlertTemplateMutation,
    useDeleteEnsAlertTemplateMutation,
    useGetEnsConfigurationQuery,
    useGetEnsPresetsQuery,
    useListEnsAlertTemplatesQuery,
    useUpdateEnsAlertTemplateMutation,
} from "../../../store/features/ens/ensService";
import {
    EnsAlertTemplate,
    EnsAlertType,
    EnsChannel,
    EnsLevelInputDto,
} from "../../../store/features/ens/types";
import {formatDuration, formatTargetSummary} from "../CreateAlertWizard/utils";
import {TYPE_LABEL} from "../utils";
import {TemplateEditorModal} from "./TemplateEditorModal";
import {buildSaveTemplateRequest, filterTemplates, templateKey} from "./templateUtils";
import {useGetApiKeysQuery} from "../../../store/features/integrations/integrationsService";
import {ApiTriggerSetupWizard} from "./ApiTriggerSetupWizard";
import {ApiTriggerTileState, getApiTriggerTileState} from "./apiTriggerSetupUtils";

interface Props {
    onUseTemplate: (template: EnsAlertTemplate) => void;
}

const CATEGORY_STYLE: Record<EnsAlertType, { color: string; bg: string }> = {
    FRAUD: {color: '#be123c', bg: '#fff1f2'},
    TASK: {color: '#15803d', bg: '#f0fdf4'},
    POLICY: {color: '#1d4ed8', bg: '#eff6ff'},
    CUSTOM: {color: '#7c3aed', bg: '#faf5ff'},
};

const CHANNEL_SHORT_LABEL: Record<EnsChannel, string> = {
    PUSH: 'Push',
    INTERNAL_CALL: 'ASSIST',
    SMS: 'SMS',
    PSTN: 'Phone',
    WHATSAPP: 'WhatsApp',
};

const templateTargetSummary = (template: EnsAlertTemplate): string =>
    formatTargetSummary(template.targetGroupIds.length, Math.max(template.targetUsers.length, template.targetUserIds.length));

const hasTarget = (template: EnsAlertTemplate): boolean =>
    template.targetGroupIds.length > 0 || template.targetUserIds.length > 0 || template.targetUsers.length > 0;

const CategoryBadge = ({type}: { type: EnsAlertType }) => {
    const {color, bg} = CATEGORY_STYLE[type];
    return (
        <span
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold"
            style={{color, background: bg}}
        >
            <span className="h-1.5 w-1.5 rounded-full" style={{background: color}}/>
            {TYPE_LABEL[type]}
        </span>
    );
};

const EscalationChain = ({levels}: { levels: EnsLevelInputDto[] }) => {
    if (levels.length === 0) {
        return <div className="text-xs text-ink-muted">No escalation levels</div>;
    }
    const ordered = [...levels].sort((a, b) => a.sequenceNo - b.sequenceNo);
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {ordered.map((level, index) => (
                <Fragment key={level.sequenceNo}>
                    {index > 0 && (
                        <div className="flex min-w-[34px] flex-col items-center leading-none">
                            <span className="text-[10px] font-bold text-ink-muted">
                                {formatDuration(level.delaySecondsFromPrevious)}
                            </span>
                            <ArrowRight size={14} className="text-ink-disabled"/>
                        </div>
                    )}
                    <div className="flex h-[26px] items-center gap-1.5 rounded-md border border-line bg-surface-muted px-2.5">
                        <span className="text-[10px] font-bold text-brand-ink">L{level.sequenceNo}</span>
                        <span className="text-[11px] font-semibold text-ink">{CHANNEL_SHORT_LABEL[level.channel]}</span>
                    </div>
                </Fragment>
            ))}
        </div>
    );
};

const TemplateCard = ({
    template,
    onEdit,
    onDelete,
    onUseTemplate,
    apiState,
    apiKeysLoading,
    apiKeysError,
    onApiSetup,
    onRetryApiKeys,
}: {
    template: EnsAlertTemplate;
    onEdit: (template: EnsAlertTemplate) => void;
    onDelete: (template: EnsAlertTemplate) => void;
    onUseTemplate: (template: EnsAlertTemplate) => void;
    apiState: ApiTriggerTileState;
    apiKeysLoading: boolean;
    apiKeysError: boolean;
    onApiSetup: (template: EnsAlertTemplate, state: ApiTriggerTileState) => void;
    onRetryApiKeys: () => void;
}) => (
    <article
        className="flex flex-col gap-3 rounded-[10px] border border-line bg-surface px-4 py-3.5 transition-colors hover:border-brand-focus"
    >
        <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-ink">{template.name}</span>
                    <CategoryBadge type={template.type}/>
                </div>
                {template.message && (
                    <div className="line-clamp-2 text-xs leading-5 text-ink-muted">{template.message}</div>
                )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
                <PhoneHQButton
                    variant="secondary"
                    size="icon"
                    title="Edit template"
                    aria-label="Edit template"
                    className="!h-[30px] !w-[30px] !rounded-md hover:!border-brand-focus hover:!bg-brand-tint-subtle"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(template);
                    }}
                >
                    <Pencil size={14} className="!p-0 text-brand-ink"/>
                </PhoneHQButton>
                {template.id != null && (
                    <PhoneHQButton
                        variant="secondary"
                        size="icon"
                        title="Delete template"
                        aria-label="Delete template"
                        className="!h-[30px] !w-[30px] !rounded-md hover:!border-danger-tint hover:!bg-danger-tint"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(template);
                        }}
                    >
                        <Trash2 size={14} className="!p-0 text-danger-ink"/>
                    </PhoneHQButton>
                )}
            </div>
        </div>

        <div className="border-t border-line pt-3">
            {(hasTarget(template) || template.voicePrompt) && (
                <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
                    {hasTarget(template) && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-muted px-2 py-0.5 text-[11px] font-semibold text-ink-secondary">
                            <Users size={12} className="text-ink-muted"/>
                            {templateTargetSummary(template)}
                        </span>
                    )}
                    {template.voicePrompt && (
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#e9d5ff] bg-[#faf5ff] px-2 py-0.5 text-[11px] font-semibold text-[#7c3aed]"
                            title={template.voicePrompt.originalFileName}
                        >
                            <Mic size={12}/>
                            Voice prompt
                        </span>
                    )}
                </div>
            )}
            <EscalationChain levels={template.levels}/>
        </div>
        <div className="flex flex-col gap-2 border-t border-line pt-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold ${template.apiTriggerEnabled ? 'bg-brand-tint-subtle text-brand-ink' : 'bg-surface-muted text-ink-secondary'}`}>
                    <KeyRound size={12}/>
                    API trigger {template.apiTriggerEnabled ? 'on' : 'off'}
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <PhoneHQButton variant="secondary" size="sm" onClick={() => onUseTemplate(template)}>
                    Use template
                </PhoneHQButton>
                <PhoneHQButton
                    variant="secondary"
                    size="sm"
                    disabled={template.apiTriggerEnabled && apiKeysLoading}
                    onClick={() => template.apiTriggerEnabled && apiKeysError
                        ? onRetryApiKeys()
                        : onApiSetup(template, apiState)
                    }
                >
                    {template.apiTriggerEnabled && apiKeysLoading
                        ? 'Checking API access…'
                        : template.apiTriggerEnabled && apiKeysError
                            ? 'Retry API access'
                            : apiState === 'off'
                                ? 'Set up API trigger'
                                : apiState === 'needs-key'
                                    ? 'Finish setup'
                                    : 'View API instructions'}
                </PhoneHQButton>
            </div>
        </div>
    </article>
);

export const EnsTemplates = ({onUseTemplate}: Props) => {
    const [phrase, setPhrase] = useState('');
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorTemplate, setEditorTemplate] = useState<EnsAlertTemplate | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<EnsAlertTemplate | null>(null);
    const [apiSetup, setApiSetup] = useState<{template: EnsAlertTemplate; state: ApiTriggerTileState} | null>(null);

    const {data: templates, isLoading} = useListEnsAlertTemplatesQuery();
    const {data: presets} = useGetEnsPresetsQuery();
    const {data: ensConfiguration} = useGetEnsConfigurationQuery();
    const [createTemplate, {isLoading: creating}] = useCreateEnsAlertTemplateMutation();
    const [updateTemplate, {isLoading: updating}] = useUpdateEnsAlertTemplateMutation();
    const [deleteTemplate, {isLoading: deleting}] = useDeleteEnsAlertTemplateMutation();
    const {data: apiKeys, isLoading: apiKeysLoading, isError: apiKeysError, refetch: refetchApiKeys} = useGetApiKeysQuery();

    const filteredTemplates = useMemo(
        () => filterTemplates(templates ?? [], phrase, false),
        [phrase, templates],
    );
    const presetByType = useMemo(() => {
        const map = new Map<EnsAlertType, EnsLevelInputDto[]>();
        (presets ?? []).forEach((preset) => map.set(preset.type, preset.levels));
        return map;
    }, [presets]);

    const isSaving = creating || updating;
    const smsSenderConfigured = ensConfiguration?.smsSenderMsisdn != null;
    const loading = isLoading && templates == null;

    const openNewTemplate = () => {
        setEditorTemplate(null);
        setEditorOpen(true);
    };

    const openEditTemplate = (template: EnsAlertTemplate) => {
        setEditorTemplate(template);
        setEditorOpen(true);
    };

    const closeEditor = () => {
        setEditorOpen(false);
        setEditorTemplate(null);
    };

    const handleSave = async (template: EnsAlertTemplate) => {
        try {
            const request = buildSaveTemplateRequest(template);
            if (template.id == null) {
                await createTemplate(request).unwrap();
            } else {
                await updateTemplate({id: template.id, body: request}).unwrap();
            }
            closeEditor();
        } catch {
            // toast handled in service
        }
    };

    const confirmDelete = async () => {
        if (deleteTarget?.id == null) return;
        try {
            await deleteTemplate(deleteTarget.id).unwrap();
            setDeleteTarget(null);
        } catch {
            // toast handled in service
        }
    };

    return (
        <div className="flex flex-col gap-4 px-5 py-6">
            {/* Toolbar */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex h-9 flex-1 items-center gap-2 rounded-lg bg-surface-muted px-2.5">
                    <Search size={16} className="shrink-0 text-ink-muted"/>
                    <PhoneHQInput
                        id="ensTemplates-searchInput"
                        type="search"
                        value={phrase}
                        placeholder="Search templates…"
                        onChange={(e) => setPhrase(e.target.value)}
                        className="!h-7 flex-1 !border-0 !bg-transparent !px-0 !ring-0 focus:!ring-0 focus-visible:!ring-0 [&::-webkit-search-cancel-button]:appearance-none"
                    />
                </div>
                <PhoneHQButton variant="primary" size="sm" onClick={openNewTemplate} className="shrink-0">
                    <Plus size={15} className={'!p-0'} color='white'/>
                    New template
                </PhoneHQButton>
            </div>

            {loading && (
                <div className="flex items-center justify-center py-12">
                    <PhoneHQLoader/>
                </div>
            )}

            {!loading && (
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                    {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template' : 'templates'}
                </div>
            )}

            {!loading && filteredTemplates.length === 0 && (
                <div className="px-4 py-14 text-center">
                    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-surface-muted text-ink-muted">
                        <FileText size={20}/>
                    </div>
                    <div className="text-sm font-semibold text-ink">
                        {phrase.trim() ? 'No templates match your search' : 'No templates yet'}
                    </div>
                    <div className="mt-1 text-sm text-ink-muted">
                        {phrase.trim()
                            ? 'Try a different keyword.'
                            : 'Create a template to reuse escalation settings across alerts.'}
                    </div>
                </div>
            )}

            {!loading && filteredTemplates.length > 0 && (
                <div className="grid gap-2.5">
                    {filteredTemplates.map((template) => (
                        <TemplateCard
                            key={templateKey(template)}
                            template={template}
                            onEdit={openEditTemplate}
                            onDelete={setDeleteTarget}
                            onUseTemplate={onUseTemplate}
                            apiState={getApiTriggerTileState(template.apiTriggerEnabled, apiKeys?.data ?? [])}
                            apiKeysLoading={apiKeysLoading}
                            apiKeysError={apiKeysError}
                            onApiSetup={(selected, state) => setApiSetup({template: selected, state})}
                            onRetryApiKeys={() => refetchApiKeys()}
                        />
                    ))}
                </div>
            )}

            <PhoneHQDialog
                open={deleteTarget != null}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title="Delete template"
                theme="light"
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm leading-relaxed text-ink-secondary">
                        Delete <span className="font-semibold text-ink">{deleteTarget?.name}</span>? This won't
                        affect alerts already sent from it.
                    </p>
                    <PhoneHQDialogFooter>
                        <PhoneHQButton variant="secondary" size="sm" onClick={() => setDeleteTarget(null)}>
                            Cancel
                        </PhoneHQButton>
                        <PhoneHQButton
                            variant="primary"
                            size="sm"
                            disabled={deleting}
                            onClick={confirmDelete}
                            style={{background: '#dc2626', borderColor: '#dc2626'}}
                        >
                            {deleting ? 'Deleting…' : 'Delete'}
                        </PhoneHQButton>
                    </PhoneHQDialogFooter>
                </div>
            </PhoneHQDialog>

            <TemplateEditorModal
                isOpen={editorOpen}
                isSaving={isSaving}
                presetByType={presetByType}
                smsSenderConfigured={smsSenderConfigured}
                template={editorTemplate}
                onClose={closeEditor}
                onSave={handleSave}
            />
            <ApiTriggerSetupWizard
                open={apiSetup != null}
                template={apiSetup?.template ?? null}
                entryState={apiSetup?.state ?? 'off'}
                onOpenChange={(open) => {
                    if (!open) setApiSetup(null);
                }}
            />
        </div>
    );
};
